import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Chip,
  IconButton,
  Button,
} from "@mui/material";
import {
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  MailOutline as MailOutlineIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import { useOutlookPlugin } from "../../hooks/useOutlookPlugin";

export interface ShipmentAttachment {
  attachmentId: string;
  filename: string;
  contentType?: string;
  size?: number;
  emailId?: string;
  emailFrom?: string;
  emailDate?: string;
  emailSubject?: string;
  downloadUrl?: string;
  emailMessageId?: string;
}

export const EmailPreviewAttachmentPicker: React.FC<{
  shipmentId: string;
  onAttach: (attachmentId: string) => Promise<void>;
  onClose: () => void;
  existingAttachmentIds?: string[];
}> = ({ shipmentId, onAttach, onClose, existingAttachmentIds = [] }) => {
  const { apiClient } = useOutlookPlugin();
  const [attachments, setAttachments] = useState<ShipmentAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [attaching, setAttaching] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAttachments = async () => {
      setLoading(true);
      try {
        const data = await apiClient.get<{ attachments: ShipmentAttachment[] }>(
          `/email-automation/actions/shipment-attachments?shipmentId=${encodeURIComponent(shipmentId)}`,
        );
        if (!cancelled) setAttachments(data.attachments);
      } catch {
        // leave empty
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAttachments();
    return () => { cancelled = true; };
  }, [shipmentId, apiClient]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return attachments
      .filter((a) => !existingAttachmentIds.includes(a.attachmentId))
      .filter((a) => !q || a.filename.toLowerCase().includes(q))
      .sort((a, b) => {
        if (!a.emailDate && !b.emailDate) return 0;
        if (!a.emailDate) return 1;
        if (!b.emailDate) return -1;
        return new Date(b.emailDate).getTime() - new Date(a.emailDate).getTime();
      });
  }, [attachments, search, existingAttachmentIds]);

  const handleAttach = async (att: ShipmentAttachment) => {
    setAttaching(att.attachmentId);
    try {
      await onAttach(att.attachmentId);
      onClose();
    } catch {
      // error handled upstream
    } finally {
      setAttaching(null);
    }
  };

  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300,
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "#fff",
          width: "calc(100% - 12px)",
          maxHeight: "70vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            py: 0.75,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AttachFileIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Add Attachment from Shipment History
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ px: 1.5, pb: 1.5, pt: 1, overflow: "auto" }}>
          <TextField
            size="small"
            placeholder="Search by filename..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 0.5, fontSize: 18, color: "text.secondary" }} />,
              sx: { fontSize: "0.8rem" },
            }}
            sx={{ mb: 1 }}
          />

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!loading && filtered.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              {attachments.length === 0
                ? "No attachments found for this shipment."
                : "No matching attachments."}
            </Typography>
          )}

          {!loading && filtered.length > 0 && filtered.map((att) => (
            <Box
              key={att.attachmentId}
              sx={{
                borderBottom: "1px solid",
                borderColor: "divider",
                py: 1,
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              {/* Left: attachment info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Chip
                  icon={<AttachFileIcon sx={{ fontSize: "14px !important" }} />}
                  label={att.filename}
                  size="small"
                  clickable={!!att.downloadUrl}
                  onClick={att.downloadUrl ? () => window.open(att.downloadUrl, "_blank") : undefined}
                  sx={{ fontSize: "0.75rem", maxWidth: "100%" }}
                />
                {(att.emailDate || att.emailFrom) && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", mt: 0.5, fontSize: "0.7rem", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {att.emailDate && `Received ${formatDistanceToNow(new Date(att.emailDate), { addSuffix: true })}`}
                    {att.emailDate && att.emailFrom && " from "}
                    {!att.emailDate && att.emailFrom && "From "}
                    {att.emailFrom}
                  </Typography>
                )}
                {att.emailSubject && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                    <MailOutlineIcon sx={{ fontSize: 13, color: "text.secondary", flexShrink: 0 }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ fontSize: "0.7rem", overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {att.emailSubject}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Right: attach button */}
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleAttach(att)}
                disabled={attaching === att.attachmentId}
                sx={{ textTransform: "none", fontSize: "0.7rem", height: 24, minWidth: 48, flexShrink: 0, mt: 0.25, px: 1 }}
              >
                {attaching === att.attachmentId ? <CircularProgress size={14} /> : "+ Add"}
              </Button>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
